<template>
  <div v-if="hasDetails" :class="[$style.config, $style.details]">
    <template v-for="detail in computedDetails">
      <div v-if="detail.value" :key="detail.label" :class="$style.detail">
        <div v-if="detail.label" :class="$style.label">{{ detail.label }}:</div>
        <div :class="$style.value">
          <MarkedParser
            v-if="detail.markdown"
            :class="$style.markdown"
            :md="detail.value"
          />
          <template v-else>{{ detail.value }}</template>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { TypeProductWithoutUnresolvableLinksResponse } from '~/types/contentful'

const { localizedStrings } = useLocalizedStrings()

const props = defineProps<{
  data: TypeProductWithoutUnresolvableLinksResponse | undefined
}>()

type Detail = {
  label: string
  value?: string
  markdown?: boolean
}

// Computed
const computedDetails = computed<Detail[]>(() => {
  const ls = localizedStrings.value
  const upcFull = props.data?.fields?.upcFull
  const skuFull = props.data?.fields?.skuFull
  return [
    {
      label: ls['case-configuration'] || 'Case Configuration',
      value: props.data?.fields?.caseConfiguration,
    },
    {
      label: ls['pack-dimensions'] || 'Pack Dimensions',
      value: props.data?.fields?.dimensions,
    },
    {
      label: ls['pack-weight'] || 'Pack Weight',
      value: props.data?.fields?.weight,
    },
    {
      label: ls['languages-available'] || 'Languages Available',
      value: props.data?.fields?.languages?.join(', '),
    },
    {
      label: ls['upc'] || 'UPC',
      value: upcFull || props.data?.fields?.upc,
      markdown: Boolean(upcFull),
    },
    {
      label: ls['sku'] || 'SKU',
      value: skuFull || props.data?.fields?.sku,
      markdown: Boolean(skuFull),
    },
    {
      label: ls['isbn'] || 'ISBN',
      value: props.data?.fields?.isbn,
    },
    {
      label: ls['msrp'] || 'MSRP',
      value: props.data?.fields?.msrp,
    },
  ]
})

const hasDetails = computed(() =>
  computedDetails.value?.some?.((detail) => detail.value),
)
</script>

<style lang="scss" module>
.details {
  border: solid var(--gray-6);
  border-width: 1px 0 0 1px;
  display: flex;
  flex-wrap: wrap;
}

.detail {
  border: solid var(--gray-6);
  border-width: 0 1px 1px 0;
  flex: 0 0 100%;
  padding: 12px 20px;

  @include media('sm') {
    flex: 0 0 50%;
    padding: 12px 30px;
  }
}

.label {
  font-size: to-rem(14px);
  font-weight: bold;
  line-height: math.div(22, 14);
}

.value {
  color: var(--gray-8);
  font-size: to-rem(17px);
  line-height: math.div(26, 17);
  white-space: pre-line;

  .markdown {
    display: flex;
    flex-direction: column;

    p {
      margin: 0;
    }
  }

  @include media('sm') {
    font-size: to-rem(13px);
    line-height: math.div(20, 13);
  }
}
</style>
